import { RotateCcw, SquarePen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useUpdatePromptSettings } from "@/api/mutations";
import { usePromptSettings } from "@/api/queries";
import type { PromptSection, PromptSettings } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { Section } from "@/components/feedback/key-value";
import { QueryState } from "@/components/feedback/query-state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/dates";
import { formErrorsFrom } from "@/lib/form-errors";

/** Per section: its own text (custom) or null (inherits). */
type Drafts = Record<string, string | null>;

const SOURCE_LABEL = { DEFAULT: "Built-in", GLOBAL: "Global", ORGANIZATION: "This organization" } as const;

/**
 * The parts of Rogue's system prompt admins edit. Global (organizationId null) applies to every
 * organization; an organization's text overrides global section by section. The rest of the
 * prompt (honesty, money and negotiation, actions, output) is fixed. Only `prompts:manage`
 * (super admins, admins) edits; the next AI run uses the new text.
 */
export function PromptSettingsEditor({ organizationId }: { organizationId: string | null }) {
  const q = usePromptSettings(organizationId);
  return (
    <QueryState query={q} data={q.data} resource="prompt settings">
      {(settings) => <Editor key={settings.updatedAt ?? "none"} settings={settings} organizationId={organizationId} />}
    </QueryState>
  );
}

function Editor({ settings, organizationId }: { settings: PromptSettings; organizationId: string | null }) {
  const { can } = usePermissions();
  const canEdit = can("prompts:manage");
  const saved = useMemo<Drafts>(() => Object.fromEntries(settings.sections.map((s) => [s.key, s.override])), [settings]);
  const [drafts, setDrafts] = useState<Drafts>(saved);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdatePromptSettings(organizationId);
  useEffect(() => setDrafts(saved), [saved]);

  const changes = Object.fromEntries(Object.entries(drafts).filter(([k, v]) => v !== saved[k]));
  const dirty = Object.keys(changes).length > 0;
  const scopeName = organizationId ? (settings.organization?.name ?? "this organization") : "every organization";

  const save = () =>
    update.mutate(changes, {
      onSuccess: () => {
        setErrors({});
        setFormError(null);
        toast.success("Prompt updated", { description: `Rogue uses it from the next reply (${scopeName}).` });
      },
      onError: (err) => {
        const { fields, form } = formErrorsFrom(err);
        setErrors(fields);
        setFormError(form);
      },
    });

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {organizationId
          ? "These parts of Rogue's prompt can be set for this organization: each one it sets replaces the global text; the rest is inherited. "
          : "These parts of Rogue's prompt apply to every organization unless it sets its own. "}
        Honesty, money and negotiation, actions and output rules are fixed and can't be changed here.
        {settings.updatedAt ? ` Last changed ${formatDateTime(settings.updatedAt)}${settings.updatedBy ? ` by ${settings.updatedBy.name}` : ""}.` : ""}
      </p>
      {formError ? (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      ) : null}
      {settings.sections.map((section) => (
        <SectionEditor
          key={section.key}
          section={section}
          scope={settings.scope}
          value={drafts[section.key] ?? null}
          error={errors[section.key]}
          canEdit={canEdit}
          onChange={(v) => setDrafts((d) => ({ ...d, [section.key]: v }))}
        />
      ))}
      {canEdit ? (
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background/95 py-2 backdrop-blur">
          {dirty ? <span className="mr-auto text-muted-foreground text-xs">{Object.keys(changes).length} unsaved change(s)</span> : null}
          <Button variant="ghost" size="sm" disabled={!dirty || update.isPending} onClick={() => setDrafts(saved)}>
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || update.isPending} onClick={save}>
            {update.isPending ? <Spinner data-icon="inline-start" /> : null}
            Save prompt
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function SectionEditor({
  section,
  scope,
  value,
  error,
  canEdit,
  onChange,
}: {
  section: PromptSection;
  scope: PromptSettings["scope"];
  value: string | null;
  error?: string;
  canEdit: boolean;
  onChange: (value: string | null) => void;
}) {
  // What this scope inherits when it has no text of its own.
  const inherited = scope === "ORGANIZATION" ? (section.global ?? section.default) : section.default;
  const inheritedFrom = scope === "ORGANIZATION" && section.global !== null ? "global" : "built-in default";
  const custom = value !== null;
  const id = `prompt-${section.key}`;
  return (
    <Section
      title={section.label}
      actions={
        <span className="flex items-center gap-2">
          <Badge variant={custom ? "default" : "outline"} data-testid={`source-${section.key}`}>
            {custom ? (scope === "ORGANIZATION" ? SOURCE_LABEL.ORGANIZATION : SOURCE_LABEL.GLOBAL) : `Inherits ${inheritedFrom}`}
          </Badge>
          {canEdit ? (
            custom ? (
              <Button size="xs" variant="ghost" onClick={() => onChange(null)} aria-label={`Use the inherited ${section.label}`}>
                <RotateCcw data-icon="inline-start" />
                Use inherited
              </Button>
            ) : (
              <Button size="xs" variant="outline" onClick={() => onChange(inherited)} aria-label={`Customize ${section.label}`}>
                <SquarePen data-icon="inline-start" />
                Customize
              </Button>
            )
          ) : null}
        </span>
      }
    >
      <p className="mb-2 text-muted-foreground text-xs">{section.description}</p>
      {custom && canEdit ? (
        <>
          {section.multiline ? (
            <Textarea
              id={id}
              aria-label={section.label}
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              rows={Math.min(14, Math.max(3, (value ?? "").split("\n").length + 1))}
              className="font-mono text-xs"
              aria-invalid={Boolean(error)}
            />
          ) : (
            <Input id={id} aria-label={section.label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} aria-invalid={Boolean(error)} />
          )}
          <div className="mt-1 flex justify-between text-[11px]">
            <span className="text-destructive">{error}</span>
            <span className={(value ?? "").length > section.maxLength ? "text-destructive" : "text-muted-foreground"}>
              {(value ?? "").length}/{section.maxLength}
            </span>
          </div>
        </>
      ) : (
        <pre className="whitespace-pre-wrap rounded-md bg-muted px-3 py-2 font-mono text-xs" data-testid={`text-${section.key}`}>
          {(custom ? value : inherited) || <span className="text-muted-foreground">Nothing (no extra instructions)</span>}
        </pre>
      )}
    </Section>
  );
}
